import { useState, useEffect, useRef, useCallback } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { aadhaarAPI } from '../services/api';
import { 
  QrCode, 
  Camera, 
  CheckCircle, 
  XCircle, 
  Loader2, 
  AlertTriangle,
  RefreshCw,
  User,
  Calendar,
  MapPin,
  Fingerprint,
  ShieldCheck,
  Upload,
  Image as ImageIcon
} from 'lucide-react';

/**
 * Aadhaar QR Scanner Component
 * 
 * Supports two verification methods:
 * 1. Live camera scanning - scan QR code from physical Aadhaar card
 * 2. Photo upload - upload image of Aadhaar card back (with QR code)
 */
export const AadhaarQRScanner = ({ onVerificationComplete, onClose }) => {
  const [scannerState, setScannerState] = useState('idle'); // idle, scanning, processing, decoded, confirming, verified, error
  const [scannedData, setScannedData] = useState(null);
  const [error, setError] = useState(null);
  const [cameras, setCameras] = useState([]);
  const [selectedCamera, setSelectedCamera] = useState(null);
  const [qrData, setQrData] = useState(null);
  const [verificationMethod, setVerificationMethod] = useState(null); // 'camera' or 'upload'
  const [uploadedImage, setUploadedImage] = useState(null);
  const [isProcessingUpload, setIsProcessingUpload] = useState(false);
  
  const html5QrCodeRef = useRef(null);
  const fileInputRef = useRef(null);
  const scannerContainerRef = useRef(null);

  // Initialize - get available cameras
  useEffect(() => {
    Html5Qrcode.getCameras()
      .then((devices) => {
        if (devices && devices.length > 0) {
          setCameras(devices);
          const backCamera = devices.find(d => 
            d.label.toLowerCase().includes('back') || 
            d.label.toLowerCase().includes('rear') ||
            d.label.toLowerCase().includes('environment')
          );
          setSelectedCamera(backCamera?.id || devices[0].id);
        }
      })
      .catch((err) => {
        console.error('Camera enumeration error:', err);
        // Don't show error - user can still use upload method
      });

    return () => {
      stopScanner();
    };
  }, []);

  const stopScanner = useCallback(() => {
    if (html5QrCodeRef.current) {
      html5QrCodeRef.current.stop().catch(() => {});
      html5QrCodeRef.current = null;
    }
  }, []);

  // Start camera scanner - called after DOM is ready
  const initializeScanner = useCallback(async () => {
    if (!selectedCamera) {
      setError('No camera available. Please use the upload option.');
      setScannerState('error');
      return;
    }

    try {
      // Wait a tick for DOM to be ready
      await new Promise(resolve => setTimeout(resolve, 100));
      
      const element = document.getElementById('qr-reader');
      if (!element) {
        throw new Error('Scanner element not ready');
      }

      if (html5QrCodeRef.current) {
        await html5QrCodeRef.current.stop().catch(() => {});
      }

      const html5QrCode = new Html5Qrcode('qr-reader');
      html5QrCodeRef.current = html5QrCode;

      await html5QrCode.start(
        selectedCamera,
        {
          fps: 10,
          qrbox: { width: 250, height: 250 },
          aspectRatio: 1,
        },
        async (decodedText) => {
          await html5QrCode.stop();
          html5QrCodeRef.current = null;
          setQrData(decodedText);
          await processQRData(decodedText);
        },
        () => {} // Ignore scan errors
      );
    } catch (err) {
      console.error('Scanner init error:', err);
      setError('Failed to start camera. Try uploading an image instead.');
      setScannerState('error');
    }
  }, [selectedCamera]);

  // Effect to start scanner when state changes to scanning
  useEffect(() => {
    if (scannerState === 'scanning' && verificationMethod === 'camera') {
      initializeScanner();
    }
    return () => {
      if (scannerState !== 'scanning') {
        stopScanner();
      }
    };
  }, [scannerState, verificationMethod, initializeScanner, stopScanner]);

  const startCameraScanner = () => {
    setVerificationMethod('camera');
    setError(null);
    setScannedData(null);
    setScannerState('scanning');
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError('Image too large. Please select an image under 10MB.');
      return;
    }

    setVerificationMethod('upload');
    setUploadedImage(URL.createObjectURL(file));
    setIsProcessingUpload(true);
    setError(null);
    setScannerState('processing');

    try {
      // Use html5-qrcode to scan from file
      const html5QrCode = new Html5Qrcode('qr-file-scanner', { verbose: false });
      
      const decodedText = await html5QrCode.scanFile(file, true);
      html5QrCode.clear();
      
      setQrData(decodedText);
      await processQRData(decodedText);
    } catch (err) {
      console.error('File scan error:', err);
      setError('Could not detect QR code in the image. Please ensure the QR code is clearly visible and try again.');
      setScannerState('error');
    } finally {
      setIsProcessingUpload(false);
    }
  };

  const processQRData = async (qrDataText) => {
    setScannerState('processing');
    setError(null);

    try {
      const response = await aadhaarAPI.verifyQR(qrDataText, false);
      
      if (response.data.status === 'decoded') {
        setScannedData(response.data.data);
        setScannerState('decoded');
      } else {
        throw new Error('Unexpected response from server');
      }
    } catch (err) {
      console.error('QR verification error:', err);
      const errorMsg = err.response?.data?.detail || err.message || 'Failed to verify QR code';
      setError(errorMsg);
      setScannerState('error');
    }
  };

  const confirmVerification = async () => {
    if (!qrData) {
      setError('No QR data to confirm');
      return;
    }

    setScannerState('confirming');
    setError(null);

    try {
      const response = await aadhaarAPI.confirm(qrData);
      
      if (response.data.status === 'verified') {
        setScannerState('verified');
        toast.success('Aadhaar verified successfully!');
        
        setTimeout(() => {
          onVerificationComplete?.(response.data);
        }, 1500);
      } else {
        throw new Error('Verification failed');
      }
    } catch (err) {
      console.error('Confirmation error:', err);
      const errorMsg = err.response?.data?.detail || err.message || 'Failed to link Aadhaar';
      setError(errorMsg);
      setScannerState('error');
    }
  };

  const resetScanner = () => {
    stopScanner();
    setScannedData(null);
    setQrData(null);
    setError(null);
    setVerificationMethod(null);
    setUploadedImage(null);
    setScannerState('idle');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Render Methods
  const renderIdleState = () => (
    <div className="py-4">
      <div className="text-center mb-6">
        <div className="w-16 h-16 mx-auto mb-3 bg-gold/10 rounded-full flex items-center justify-center">
          <QrCode className="w-8 h-8 text-gold" />
        </div>
        <h3 className="font-semibold text-navy mb-1">Verify with Aadhaar QR</h3>
        <p className="text-sm text-muted-foreground">
          Scan or upload the QR code from your Aadhaar card
        </p>
      </div>

      {/* Two Options */}
      <div className="space-y-3">
        {/* Camera Option */}
        <Button 
          onClick={startCameraScanner}
          className="w-full h-14 gold-gradient text-navy font-semibold justify-start px-4"
          disabled={cameras.length === 0}
          data-testid="start-camera-scan-btn"
        >
          <Camera className="w-5 h-5 mr-3" />
          <div className="text-left">
            <div>Scan with Camera</div>
            <div className="text-xs font-normal opacity-80">
              {cameras.length > 0 ? 'Point camera at Aadhaar QR' : 'No camera detected'}
            </div>
          </div>
        </Button>

        {/* Upload Option */}
        <Button 
          onClick={() => fileInputRef.current?.click()}
          variant="outline"
          className="w-full h-14 justify-start px-4 border-gold/30 hover:bg-gold/5"
          data-testid="upload-aadhaar-btn"
        >
          <Upload className="w-5 h-5 mr-3 text-gold" />
          <div className="text-left">
            <div className="text-navy">Upload Aadhaar Image</div>
            <div className="text-xs text-muted-foreground">
              Upload photo of Aadhaar back side
            </div>
          </div>
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileSelect}
          className="hidden"
          data-testid="aadhaar-file-input"
        />
      </div>

      {/* Hidden element for file QR scanning */}
      <div id="qr-file-scanner" style={{ display: 'none' }} />

      {/* Tips */}
      <div className="mt-6 p-3 bg-gray-50 rounded-lg">
        <p className="text-xs font-medium text-navy mb-2">Tips for best results:</p>
        <ul className="text-xs text-muted-foreground space-y-1">
          <li className="flex items-start gap-2">
            <CheckCircle className="w-3 h-3 text-green-500 mt-0.5 flex-shrink-0" />
            Ensure the QR code is clearly visible
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle className="w-3 h-3 text-green-500 mt-0.5 flex-shrink-0" />
            Use good lighting, avoid shadows
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle className="w-3 h-3 text-green-500 mt-0.5 flex-shrink-0" />
            QR is on the back of Aadhaar card
          </li>
        </ul>
      </div>
    </div>
  );

  const renderScanningState = () => (
    <div className="py-4">
      <div 
        id="qr-reader" 
        ref={scannerContainerRef}
        className="mx-auto mb-4 rounded-lg overflow-hidden bg-black"
        style={{ maxWidth: '300px', minHeight: '300px' }}
      />
      <p className="text-sm text-muted-foreground text-center mb-4">
        Position the Aadhaar QR code within the frame
      </p>
      <div className="flex gap-3 justify-center">
        <Button 
          variant="outline" 
          onClick={resetScanner}
        >
          Cancel
        </Button>
        <Button 
          variant="outline"
          onClick={() => fileInputRef.current?.click()}
        >
          <Upload className="w-4 h-4 mr-2" />
          Upload Instead
        </Button>
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className="hidden"
      />
      {/* Hidden element for file scanning */}
      <div id="qr-file-scanner" style={{ display: 'none' }} />
    </div>
  );

  const renderProcessingState = () => (
    <div className="text-center py-10">
      {uploadedImage && (
        <div className="mb-4 mx-auto w-32 h-32 rounded-lg overflow-hidden border border-gray-200">
          <img src={uploadedImage} alt="Uploaded" className="w-full h-full object-cover" />
        </div>
      )}
      <Loader2 className="w-10 h-10 text-gold animate-spin mx-auto mb-3" />
      <h3 className="font-semibold text-navy mb-1">
        {uploadedImage ? 'Scanning QR from Image' : 'Verifying QR Code'}
      </h3>
      <p className="text-sm text-muted-foreground">
        {uploadedImage ? 'Extracting and verifying data...' : 'Checking UIDAI signature...'}
      </p>
      {/* Hidden element for file scanning */}
      <div id="qr-file-scanner" style={{ display: 'none' }} />
    </div>
  );

  const renderDecodedState = () => (
    <div className="py-4">
      <div className="flex items-center justify-center gap-2 mb-4">
        <CheckCircle className="w-5 h-5 text-green-500" />
        <h3 className="font-semibold text-navy">QR Code Verified</h3>
      </div>
      <p className="text-sm text-muted-foreground text-center mb-4">
        Please confirm the details below
      </p>
      
      {/* Extracted Data */}
      <div className="bg-gray-50 rounded-lg p-4 space-y-3 mb-4">
        <div className="flex items-start gap-3">
          <User className="w-4 h-4 text-gold mt-1" />
          <div>
            <p className="text-xs text-muted-foreground">Name</p>
            <p className="font-medium text-navy text-sm">{scannedData?.name || '-'}</p>
          </div>
        </div>
        
        <div className="flex items-start gap-3">
          <Fingerprint className="w-4 h-4 text-gold mt-1" />
          <div>
            <p className="text-xs text-muted-foreground">Aadhaar Number</p>
            <p className="font-mono text-navy text-sm">XXXX XXXX {scannedData?.aadhaar_last_4 || '****'}</p>
          </div>
        </div>
        
        {scannedData?.date_of_birth && (
          <div className="flex items-start gap-3">
            <Calendar className="w-4 h-4 text-gold mt-1" />
            <div>
              <p className="text-xs text-muted-foreground">Date of Birth</p>
              <p className="text-navy text-sm">{scannedData.date_of_birth}</p>
            </div>
          </div>
        )}
        
        {scannedData?.gender && (
          <div className="flex items-start gap-3">
            <User className="w-4 h-4 text-gold mt-1" />
            <div>
              <p className="text-xs text-muted-foreground">Gender</p>
              <p className="text-navy text-sm">{scannedData.gender}</p>
            </div>
          </div>
        )}
        
        {scannedData?.address && (
          <div className="flex items-start gap-3">
            <MapPin className="w-4 h-4 text-gold mt-1" />
            <div>
              <p className="text-xs text-muted-foreground">Address</p>
              <p className="text-navy text-xs">{scannedData.address}</p>
            </div>
          </div>
        )}
      </div>
      
      {/* Warning */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
        <div className="flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-amber-800">
            This Aadhaar will be permanently linked to your account.
          </p>
        </div>
      </div>
      
      {/* Actions */}
      <div className="flex gap-3">
        <Button variant="outline" onClick={resetScanner} className="flex-1">
          <RefreshCw className="w-4 h-4 mr-2" />
          Retry
        </Button>
        <Button 
          onClick={confirmVerification} 
          className="flex-1 gold-gradient text-navy font-semibold"
        >
          <ShieldCheck className="w-4 h-4 mr-2" />
          Confirm
        </Button>
      </div>
    </div>
  );

  const renderConfirmingState = () => (
    <div className="text-center py-10">
      <Loader2 className="w-10 h-10 text-gold animate-spin mx-auto mb-3" />
      <h3 className="font-semibold text-navy mb-1">Linking Aadhaar</h3>
      <p className="text-sm text-muted-foreground">
        Please wait...
      </p>
    </div>
  );

  const renderVerifiedState = () => (
    <div className="text-center py-10">
      <div className="w-16 h-16 mx-auto mb-4 bg-green-100 rounded-full flex items-center justify-center">
        <CheckCircle className="w-10 h-10 text-green-500" />
      </div>
      <h3 className="font-semibold text-navy text-lg mb-2">Aadhaar Verified!</h3>
      <p className="text-sm text-muted-foreground mb-3">
        Your identity has been verified
      </p>
      <Badge className="bg-green-100 text-green-800 border-green-200">
        KYC Complete
      </Badge>
    </div>
  );

  const renderErrorState = () => (
    <div className="text-center py-8">
      <div className="w-14 h-14 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
        <XCircle className="w-8 h-8 text-red-500" />
      </div>
      <h3 className="font-semibold text-navy mb-2">Verification Failed</h3>
      <p className="text-sm text-red-600 mb-6 px-2">
        {error || 'Something went wrong'}
      </p>
      <div className="flex gap-3 justify-center">
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={resetScanner} className="gold-gradient text-navy font-semibold">
          <RefreshCw className="w-4 h-4 mr-2" />
          Try Again
        </Button>
      </div>
      {/* Hidden element for file scanning */}
      <div id="qr-file-scanner" style={{ display: 'none' }} />
    </div>
  );

  const renderContent = () => {
    switch (scannerState) {
      case 'idle': return renderIdleState();
      case 'scanning': return renderScanningState();
      case 'processing': return renderProcessingState();
      case 'decoded': return renderDecodedState();
      case 'confirming': return renderConfirmingState();
      case 'verified': return renderVerifiedState();
      case 'error': return renderErrorState();
      default: return renderIdleState();
    }
  };

  return (
    <Card className="border-gold/30" data-testid="aadhaar-qr-scanner">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gold/10 rounded-lg flex items-center justify-center">
              <Fingerprint className="w-5 h-5 text-gold" />
            </div>
            <div>
              <CardTitle className="font-serif text-lg text-navy">Aadhaar Verification</CardTitle>
              <CardDescription>Verify your identity with Aadhaar QR</CardDescription>
            </div>
          </div>
          {scannerState !== 'verified' && scannerState !== 'confirming' && (
            <Button variant="ghost" size="sm" onClick={onClose}>
              <XCircle className="w-5 h-5" />
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {renderContent()}
      </CardContent>
    </Card>
  );
};

export default AadhaarQRScanner;
